/* ==========================================================================
   BAN WEATHER MOD — Core Theme JS
   ========================================================================== */

'use strict';

/* --------------------------------------------------------------------------
   Utility: debounce
   -------------------------------------------------------------------------- */
function debounce(fn, wait = 200) {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), wait);
  };
}

/* --------------------------------------------------------------------------
   Scroll state on <html> for CSS targeting
   -------------------------------------------------------------------------- */
(function initScrollState() {
  const root = document.documentElement;
  let lastY = window.scrollY;
  let ticking = false;
  // Hero threshold: hide headers only after scrolling past the viewport
  const HIDE_THRESHOLD = window.innerHeight * 0.85;

  function update() {
    const y = window.scrollY;
    const delta = y - lastY;

    root.classList.toggle('is-scrolled', y > 60);

    if (y <= 60) {
      // At/near top — always show both bars
      root.classList.remove('scroll-down');
      root.classList.remove('scroll-up');
    } else if (delta > 0 && y > HIDE_THRESHOLD) {
      // Scrolling DOWN past the hero — hide
      root.classList.add('scroll-down');
      root.classList.remove('scroll-up');
    } else if (delta < 0) {
      // Any upward scroll — immediately reappear
      root.classList.remove('scroll-down');
      root.classList.add('scroll-up');
    }

    lastY = y;
    ticking = false;
  }

  window.addEventListener('scroll', () => {
    if (!ticking) {
      requestAnimationFrame(update);
      ticking = true;
    }
  }, { passive: true });
})();

/* --------------------------------------------------------------------------
   Cart Drawer
   -------------------------------------------------------------------------- */
class CartDrawer {
  constructor() {
    this.drawer = document.getElementById('cart-drawer');
    this.overlay = document.getElementById('cart-overlay');
    this.counters = document.querySelectorAll('[data-cart-count]');

    if (!this.drawer) return;

    this.overlay?.addEventListener('click', () => this.close());
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') this.close();
    });
    document.addEventListener('click', (e) => {
      if (e.target.closest('[data-cart-toggle]')) {
        e.preventDefault();
        this.toggle();
      }
    });
  }

  open() {
    this.drawer.classList.add('is-open');
    this.overlay?.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    this.drawer.setAttribute('aria-hidden', 'false');
  }

  close() {
    this.drawer.classList.remove('is-open');
    this.overlay?.classList.remove('is-open');
    document.body.style.overflow = '';
    this.drawer.setAttribute('aria-hidden', 'true');
  }

  toggle() {
    this.drawer.classList.contains('is-open') ? this.close() : this.open();
  }

  updateCount(count) {
    this.counters.forEach(el => {
      el.textContent = count;
      el.classList.toggle('is-visible', count > 0);
    });
  }
}

/* --------------------------------------------------------------------------
   Mobile Nav
   -------------------------------------------------------------------------- */
class MobileNav {
  constructor() { /* replaced by HamburgerMenu */ }
  open()   {}
  close()  {}
  toggle() {}
}

/* --------------------------------------------------------------------------
   Hamburger Menu (slide-in panel from right)
   -------------------------------------------------------------------------- */
class HamburgerMenu {
  constructor() {
    this.menu     = document.getElementById('hamburger-menu');
    this.openBtn  = document.getElementById('hamburger-btn');
    this.closeBtn = document.getElementById('hamburger-close');
    this.backdrop = document.getElementById('hamburger-backdrop');
    this.inner    = document.getElementById('hamburger-inner');
    if (!this.menu) return;

    this._closeTimeout = null;
    const isMobile = () => window.matchMedia('(max-width: 768px)').matches;
    const isTouchDevice = () => !window.matchMedia('(hover: hover) and (pointer: fine)').matches;

    // Desktop: hover on the button OR the panel itself
    this.openBtn?.addEventListener('mouseenter', () => { if (!isMobile()) this.open(); });
    this.inner?.addEventListener('mouseenter',   () => { if (!isMobile()) { clearTimeout(this._closeTimeout); } });
    this.openBtn?.addEventListener('mouseleave', () => { if (!isMobile()) this._scheduleClose(); });
    this.inner?.addEventListener('mouseleave',   () => { if (!isMobile()) this._scheduleClose(); });

    // Mobile / touch: click
    this.openBtn?.addEventListener('click', () => { if (isMobile() || isTouchDevice()) this.toggle(); });

    this.closeBtn?.addEventListener('click',  () => this.close());
    this.backdrop?.addEventListener('click',  () => this.close());
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.close(); });
  }

  _scheduleClose() {
    this._closeTimeout = setTimeout(() => this.close(), 180);
  }

  open() {
    clearTimeout(this._closeTimeout);
    this.menu.classList.add('is-open');
    this.menu.setAttribute('aria-hidden', 'false');
    this.openBtn?.setAttribute('aria-expanded', 'true');
    // Only lock scroll on mobile
    if (window.matchMedia('(max-width: 768px)').matches) {
      document.body.style.overflow = 'hidden';
    }
  }

  close() {
    this.menu.classList.remove('is-open');
    this.menu.setAttribute('aria-hidden', 'true');
    this.openBtn?.setAttribute('aria-expanded', 'false');
    document.body.style.overflow = '';
  }

  toggle() {
    this.menu.classList.contains('is-open') ? this.close() : this.open();
  }
}

/* --------------------------------------------------------------------------
   Intersection Observer: fade-in on scroll
   Above-fold elements (inside .hero or [data-above-fold]) fire immediately.
   -------------------------------------------------------------------------- */
function initFadeIn() {
  const els = document.querySelectorAll('[data-fade-in]');
  if (!els.length) return;

  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      }
    });
  }, { threshold: 0.08, rootMargin: '0px 0px -40px 0px' });

  els.forEach(el => {
    // Hero content is always in viewport — trigger on next frame so CSS
    // transition fires rather than starting already-visible.
    if (el.closest('.hero')) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => el.classList.add('is-visible'));
      });
    } else {
      observer.observe(el);
    }
  });
}

/* --------------------------------------------------------------------------
   Subtle parallax for hero (CSS-only fallback is fine, this enhances)
   -------------------------------------------------------------------------- */
function initParallax() {
  const layers = document.querySelectorAll('[data-parallax]');
  if (!layers.length || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  let ticking = false;
  window.addEventListener('scroll', () => {
    if (!ticking) {
      requestAnimationFrame(() => {
        const y = window.scrollY;
        layers.forEach(el => {
          const speed = parseFloat(el.dataset.parallax) || 0.3;
          el.style.transform = `translateY(${y * speed}px)`;
        });
        ticking = false;
      });
      ticking = true;
    }
  }, { passive: true });
}

/* --------------------------------------------------------------------------
   Announce cart updates for screen readers
   -------------------------------------------------------------------------- */
function announceCartUpdate(message) {
  let live = document.getElementById('cart-live');
  if (!live) {
    live = document.createElement('div');
    live.id = 'cart-live';
    live.setAttribute('aria-live', 'polite');
    live.setAttribute('aria-atomic', 'true');
    live.className = 'sr-only';
    document.body.appendChild(live);
  }
  live.textContent = message;
  setTimeout(() => { live.textContent = ''; }, 3000);
}

/* --------------------------------------------------------------------------
   Active nav link detection
   -------------------------------------------------------------------------- */
function initActiveNavLinks() {
  const links = document.querySelectorAll('.site-nav__link');
  const current = window.location.pathname;
  links.forEach(link => {
    const href = link.getAttribute('href');
    if (!href) return;
    const isActive = href !== '/' && current.startsWith(href);
    if (isActive) {
      link.closest('.site-nav__item')?.classList.add('site-nav__item--active');
    }
  });
}

/* --------------------------------------------------------------------------
   Dropdown: close on outside click / Escape
   -------------------------------------------------------------------------- */
function initDropdowns() {
  // Keyboard: Escape closes any open dropdown
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      document.activeElement?.closest('.site-nav__item--has-dropdown')
        ?.querySelector('.site-nav__link')?.focus();
    }
  });
}

/* --------------------------------------------------------------------------
   Collection Row: toggle tabs + drag-to-scroll
   -------------------------------------------------------------------------- */
function initCollectionRows() {

  // Toggle tabs (Women / Men)
  document.querySelectorAll('[data-toggle]').forEach(btn => {
    btn.addEventListener('click', () => {
      const sectionId = btn.dataset.toggle;
      const panelNum  = btn.dataset.panel;

      // Update button states
      document.querySelectorAll(`[data-toggle="${sectionId}"]`).forEach(b => {
        b.classList.remove('coll-row__toggle-btn--active');
        b.setAttribute('aria-pressed', 'false');
      });
      btn.classList.add('coll-row__toggle-btn--active');
      btn.setAttribute('aria-pressed', 'true');

      // Show/hide panels
      document.querySelectorAll(`#${sectionId}-panel-1, #${sectionId}-panel-2`).forEach(panel => {
        panel.classList.add('coll-row__panel--hidden');
      });
      const target = document.getElementById(`${sectionId}-panel-${panelNum}`);
      target?.classList.remove('coll-row__panel--hidden');
    });
  });

  // Drag-to-scroll on all strips
  document.querySelectorAll('.coll-row__strip').forEach(strip => {
    let isDown = false;
    let startX, scrollLeft;

    strip.addEventListener('mousedown', e => {
      isDown = true;
      startX = e.pageX - strip.offsetLeft;
      scrollLeft = strip.scrollLeft;
      strip.style.userSelect = 'none';
    });

    strip.addEventListener('mouseleave', () => { isDown = false; });
    strip.addEventListener('mouseup',    () => { isDown = false; strip.style.userSelect = ''; });

    strip.addEventListener('mousemove', e => {
      if (!isDown) return;
      e.preventDefault();
      const x    = e.pageX - strip.offsetLeft;
      const walk = (x - startX) * 1.5;
      strip.scrollLeft = scrollLeft - walk;
    });
  });
}

/* --------------------------------------------------------------------------
   Collection Page Filters: collapsible groups
   -------------------------------------------------------------------------- */
function initCollectionFilters() {
  document.querySelectorAll('[data-filter-toggle]').forEach(btn => {
    btn.addEventListener('click', () => {
      const group = btn.closest('[data-filter-group]');
      if (!group) return;
      const open = group.classList.toggle('is-open');
      btn.setAttribute('aria-expanded', String(open));
      const icon = btn.querySelector('.cp-filter-group__icon');
      if (icon) icon.textContent = open ? '−' : '+';
    });
  });
}

/* --------------------------------------------------------------------------
   Boot
   -------------------------------------------------------------------------- */
document.addEventListener('DOMContentLoaded', () => {
  window.BWM = {
    cart:      new CartDrawer(),
    nav:       new MobileNav(),
    hamburger: new HamburgerMenu(),
  };

  initFadeIn();
  initCollectionRows();
  initCollectionFilters();
  initParallax();
  initManifestoParallax();
  initActiveNavLinks();
  initDropdowns();
  initLazyImages();
});

/* --------------------------------------------------------------------------
   Manifesto parallax — scales up when centered in viewport, fades when past
   -------------------------------------------------------------------------- */
function initManifestoParallax() {
  const section = document.querySelector('[data-manifesto-parallax]');
  if (!section) return;
  const content = section.querySelector('.manifesto__content--centered');
  if (!content) return;

  function update() {
    const rect = section.getBoundingClientRect();
    const vh = window.innerHeight;
    const mid = rect.top + rect.height / 2;
    const dist = (mid - vh / 2) / vh;
    const scale = Math.max(0.88, 1 - Math.abs(dist) * 0.28);
    const opacity = Math.max(0.4, 1 - Math.abs(dist) * 0.9);
    content.style.transform = 'scale(' + scale + ')';
    content.style.opacity = opacity;
  }

  window.addEventListener('scroll', update, { passive: true });
  update();
}

/* --------------------------------------------------------------------------
   Lazy image fade-in via native loading=lazy
   -------------------------------------------------------------------------- */
function initLazyImages() {
  const imgs = document.querySelectorAll('img[loading="lazy"]');
  imgs.forEach(img => {
    if (img.complete) {
      img.classList.add('loaded');
    } else {
      img.addEventListener('load', () => img.classList.add('loaded'), { once: true });
    }
  });
}